<template>
  <div
    class="theme-toggle"
    @click="toggleTheme"
  >
    <div class="toggle-icon">
      <!-- Sun icon for light mode -->
      <svg
        v-if="isDark"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="5"
          stroke="currentColor"
          stroke-width="2"
        />
        <path
          d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
      <!-- Moon icon for dark mode -->
      <svg
        v-else
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useThemeStore } from '@/stores/theme'
import { syncColorModeToUrl } from '@/utils/syncColorModeToUrl'

const themeStore = useThemeStore()

const isDark = computed(() => themeStore.isDark)

const toggleTheme = () => {
  themeStore.toggleMode()
  void syncColorModeToUrl(themeStore.mode)
}
</script>

<style scoped>
.theme-toggle {
  position: fixed;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  z-index: 1001;
  cursor: pointer;
  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.toggle-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bg-card);
  border: 2px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  box-shadow: 0 2px 8px var(--shadow-color);
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
  position: relative;
  z-index: 1;
}

@keyframes pulse {
  0% {
    box-shadow: 0 2px 8px var(--shadow-color);
  }
  50% {
    box-shadow: 0 2px 8px var(--shadow-color), 0 0 0 4px var(--bg-hover);
  }
  100% {
    box-shadow: 0 2px 8px var(--shadow-color);
  }
}

.toggle-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.theme-toggle:hover .toggle-icon {
  background: var(--accent-color);
  color: var(--text-white);
  border-color: var(--accent-color);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .theme-toggle {
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1002;
  }
  
  .toggle-icon {
    width: 44px;
    height: 44px;
    box-shadow: 0 4px 12px var(--shadow-color);
    border: 2px solid var(--border-color);
  }
  
  .toggle-icon svg {
    width: 22px;
    height: 22px;
  }
  
  .toggle-icon:active {
    transform: scale(0.95);
  }
}

@media (max-width: 480px) {
  .theme-toggle {
    right: 8px;
    z-index: 1002;
  }
  
  .toggle-icon {
    width: 40px;
    height: 40px;
    box-shadow: 0 2px 8px var(--shadow-color);
  }
  
  .toggle-icon svg {
    width: 20px;
    height: 20px;
  }
}

/* Ensure visibility on very small screens */
@media (max-width: 360px) {
  .theme-toggle {
    right: 4px;
  }
  
  .toggle-icon {
    width: 36px;
    height: 36px;
  }
  
  .toggle-icon svg {
    width: 18px;
    height: 18px;
  }
}
</style>
